import { useEffect, useRef, useState } from "react";
import { UploadCloud, X, FolderTree, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { uploadInChunks, CHUNK_SIZE } from "@/lib/upload";
import { useI18n } from "@/lib/i18n";
import { GoldButton } from "@/components/Brand";

const field = "min-h-[44px] w-full border border-[#C69A3B]/60 bg-white px-3 text-sm";

const folderOf = (file) => {
  const rel = file.webkitRelativePath || "";
  const parts = rel.split("/").filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 2] : "";
};

export default function AdminUpload({ onUploaded }) {
  const { t, localized } = useI18n();
  const fileRef = useRef(null);
  const folderRef = useRef(null);
  const cancelRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [eventId, setEventId] = useState("");
  const [albumId, setAlbumId] = useState("");
  const [mode, setMode] = useState("files");
  const [items, setItems] = useState([]);
  const [watermarkMode, setWatermarkMode] = useState("auto");
  const [downloadEnabled, setDownloadEnabled] = useState(true);
  const [published, setPublished] = useState(true);
  const [allowSimilar, setAllowSimilar] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [video, setVideo] = useState({ url: "", title_es: "", title_en: "", duration: "" });

  const event = events.find((e) => e.id === eventId);
  const autoWatermark = event ? Boolean(event.watermark) : false;
  const effectiveWatermark = watermarkMode === "auto" ? autoWatermark : watermarkMode === "on";

  useEffect(() => {
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const loadAlbums = async (id) => {
    const { data } = await api.get(`/admin/events/${id}/albums`);
    setAlbums(data);
    return data;
  };

  useEffect(() => {
    if (!eventId) return setAlbums([]);
    loadAlbums(eventId).then((data) => setAlbumId(data[0]?.id || ""));
  }, [eventId]);

  const addFiles = (files, withFolders) =>
    setItems((prev) => [
      ...prev,
      ...Array.from(files)
        .filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"))
        .map((f) => ({ file: f, folder: withFolders ? folderOf(f) : "", status: "pending" })),
    ]);

  const uploadBatch = async (files, targetAlbum) => {
    const controller = new AbortController();
    cancelRef.current = controller;
    return uploadInChunks({
      files,
      eventId,
      albumId: targetAlbum,
      watermark: effectiveWatermark,
      downloadEnabled,
      published,
      allowSimilar,
      signal: controller.signal,
      onProgress: setProgress,
    });
  };

  const upload = async () => {
    const pending = items.filter((i) => i.status !== "done");
    if (!eventId || !pending.length) return;
    setBusy(true);
    setProgress(0);
    try {
      let groups;
      if (mode === "folders") {
        const names = [...new Set(pending.map((i) => i.folder).filter(Boolean))];
        if (!names.length) {
          toast.error("Las carpetas no tienen nombre; use el modo de archivos.");
          setBusy(false);
          return;
        }
        const { data: map } = await api.post(`/admin/events/${eventId}/albums/ensure`, { names });
        await loadAlbums(eventId);
        const bySlug = Object.entries(map).reduce((acc, [slug, al]) => ({ ...acc, [slug]: al.id }), {});
        const slugOf = (name) =>
          Object.keys(bySlug).find((s) => s === name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""));
        groups = names.map((name) => ({
          album: bySlug[slugOf(name)],
          label: name,
          files: pending.filter((i) => i.folder === name).map((i) => i.file),
        }));
      } else {
        if (!albumId) return setBusy(false);
        groups = [{ album: albumId, label: "", files: pending.map((i) => i.file) }];
      }

      let total = 0;
      const failed = new Set();
      const duped = new Set();
      for (const group of groups) {
        if (!group.album || !group.files.length) continue;
        const data = await uploadBatch(group.files, group.album);
        total += data.uploaded;
        (data.errors || []).forEach((e) => failed.add(e.filename));
        if (data.errors?.length) toast.error(t("upload.failed").replace("{n}", data.errors.length));
        if (data.duplicates?.length) {
          data.duplicates.forEach((d) => duped.add(d.filename));
          toast.message(t("upload.duplicates").replace("{n}", data.duplicates.length), {
            description: data.duplicates.slice(0, 5).map((d) => d.filename).join(", "),
          });
        }
      }
      setItems((prev) => prev.map((i) => ({
        ...i,
        status: failed.has(i.file.name) ? "error" : duped.has(i.file.name) ? "duplicate" : "done",
      })));
      toast.success(
        `${t("upload.published").replace("{n}", total)}${mode === "folders" ? ` · ${groups.length} ${t("events.albums").toLowerCase()}` : ""}`
      );
      onUploaded?.();
    } catch (err) {
      if (err.name === "CanceledError") toast.message(t("btn.cancel"));
      else toast.error(apiError(err.response?.data?.detail));
      setItems((prev) => prev.map((i) => (i.status === "done" ? i : { ...i, status: "error" })));
    } finally {
      setBusy(false);
      cancelRef.current = null;
    }
  };

  const addVideo = async () => {
    if (!eventId || !albumId || !video.url) return;
    try {
      await api.post("/admin/media/video", {
        event_id: eventId,
        album_id: albumId,
        url: video.url,
        title_es: video.title_es || null,
        title_en: video.title_en || null,
        duration: video.duration ? parseInt(video.duration, 10) : null,
      });
      setVideo({ url: "", title_es: "", title_en: "", duration: "" });
      toast.success("Video ✓");
      onUploaded?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const folderSummary = [...new Set(items.map((i) => i.folder).filter(Boolean))];

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-upload-panel">
      <div className="border border-[#C69A3B]/40 bg-white p-6 lg:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="label-mono">Evento</span>
            <select className={field} data-testid="upload-event-select" value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="">—</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>{localized(ev, "name")}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label-mono">Álbum {mode === "folders" && "(automático por carpeta)"}</span>
            <select className={field} data-testid="upload-album-select" value={albumId} disabled={mode === "folders"} onChange={(e) => setAlbumId(e.target.value)}>
              {albums.map((a) => (
                <option key={a.id} value={a.id}>{localized(a, "name")}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-5 flex gap-2">
          {[["files", "Archivos"], ["folders", "Carpetas por álbum"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              data-testid={`upload-mode-${key}`}
              onClick={() => {
                setMode(key);
                setItems([]);
              }}
              className={`min-h-[44px] border px-4 text-sm transition-colors duration-300 ${
                mode === key ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B]/50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            addFiles(e.dataTransfer.files, mode === "folders");
          }}
          onClick={() => (mode === "folders" ? folderRef.current?.click() : fileRef.current?.click())}
          data-testid="upload-dropzone"
          className="mt-6 cursor-pointer border border-dashed border-[#C69A3B] p-10 text-center transition-colors duration-300 hover:bg-[#F6F0E4]"
        >
          {mode === "folders" ? <FolderTree className="mx-auto text-[#C69A3B]" /> : <UploadCloud className="mx-auto text-[#C69A3B]" />}
          <p className="mt-3 text-sm text-[#5A5752]">
            {items.length
              ? `${items.length} archivo(s)${folderSummary.length ? ` · ${folderSummary.length} carpeta(s)` : ""}`
              : mode === "folders"
                ? "Seleccione las carpetas con el nombre de cada álbum (Recepción, Grupos…)"
                : "Arrastre fotos o videos aquí"}
          </p>
          <input ref={fileRef} type="file" multiple accept="image/*,video/*" data-testid="upload-file-input" className="hidden" onChange={(e) => addFiles(e.target.files, false)} />
          <input ref={folderRef} type="file" multiple webkitdirectory="" directory="" data-testid="upload-folder-input" className="hidden" onChange={(e) => addFiles(e.target.files, true)} />
        </div>

        {folderSummary.length > 0 && (
          <ul className="mt-4 space-y-1 text-xs" data-testid="upload-folder-summary">
            {folderSummary.map((name) => (
              <li key={name} className="flex justify-between border-b border-[#D8D3CB] py-1.5">
                <span className="font-medium">{name}</span>
                <span className="text-[#5A5752]">{items.filter((i) => i.folder === name).length} archivo(s)</span>
              </li>
            ))}
          </ul>
        )}

        {items.length > 0 && folderSummary.length === 0 && (
          <ul className="mt-4 space-y-1 text-xs" data-testid="upload-file-list">
            {items.map((i, idx) => (
              <li key={i.file.name + idx} className="flex items-center justify-between gap-3 border-b border-[#D8D3CB] py-1.5">
                <span className="truncate">{i.file.name}</span>
                <span className="flex items-center gap-2">
                  <span className={i.status === "error" ? "text-[#8F3329]" : i.status === "duplicate" ? "text-[#C69A3B]" : "text-[#275E64]"}>{i.status === "duplicate" ? t("lab.duplicate") : i.status}</span>
                  <button type="button" aria-label="remove" onClick={() => setItems((p) => p.filter((_, x) => x !== idx))}>
                    <X size={12} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}

        <p className="mt-3 text-xs text-[#5A5752]">
          Las fotos se suben en tandas según su tamaño (hasta {CHUNK_SIZE} archivos o 20 MB por envío); puede
          seleccionar cientos de fotos a la vez, incluso archivos grandes de cámara.</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="label-mono">{t("common.watermark")}</span>
            <select className={field} data-testid="upload-watermark-mode" value={watermarkMode} onChange={(e) => setWatermarkMode(e.target.value)}>
              <option value="auto">Según la configuración del evento {event ? `(${autoWatermark ? "sí" : "no"})` : ""}</option>
              <option value="on">Siempre con marca de agua</option>
              <option value="off">Sin marca de agua</option>
            </select>
          </label>
          <div className="flex flex-wrap items-end gap-5 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" data-testid="upload-download" checked={downloadEnabled} onChange={(e) => setDownloadEnabled(e.target.checked)} />
              {t("common.downloads")}
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" data-testid="upload-published" checked={published} onChange={(e) => setPublished(e.target.checked)} />
              {t("common.published")}
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" data-testid="upload-allow-similar" checked={allowSimilar} onChange={(e) => setAllowSimilar(e.target.checked)} />
              {t("upload.allowSimilar")}
            </label>
          </div>
        </div>

        {busy && (
          <div className="mt-5 h-2 w-full bg-[#D8D3CB]" data-testid="upload-progress">
            <div className="h-full bg-[#8F3329] transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <GoldButton onClick={upload} disabled={busy || !eventId || !items.length || (mode === "files" && !albumId)} data-testid="upload-submit-btn">
            {busy ? `${progress}%` : t("btn.uploadPhotos")}
          </GoldButton>
          {busy && (
            <GoldButton variant="outline" onClick={() => cancelRef.current?.abort()} data-testid="upload-cancel-btn">
              {t("btn.cancel")}
            </GoldButton>
          )}
          {!busy && items.some((i) => i.status === "error") && (
            <GoldButton variant="outline" onClick={upload} data-testid="upload-retry-btn">
              Reintentar
            </GoldButton>
          )}
        </div>
      </div>

      <div className="border border-[#C69A3B]/40 bg-white p-6 lg:p-8" data-testid="video-link-panel">
        <p className="label-mono flex items-center gap-2">
          <Link2 size={13} /> Video por enlace (YouTube / Vimeo)
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <input className={`${field} sm:col-span-2`} placeholder="https://youtu.be/… o https://vimeo.com/…" data-testid="video-url-input" value={video.url} onChange={(e) => setVideo((v) => ({ ...v, url: e.target.value }))} />
          <input className={field} placeholder="Título — Español" data-testid="video-title-es" value={video.title_es} onChange={(e) => setVideo((v) => ({ ...v, title_es: e.target.value }))} />
          <input className={field} placeholder="Title — English" data-testid="video-title-en" value={video.title_en} onChange={(e) => setVideo((v) => ({ ...v, title_en: e.target.value }))} />
          <input className={field} type="number" placeholder="Duración (segundos)" data-testid="video-duration" value={video.duration} onChange={(e) => setVideo((v) => ({ ...v, duration: e.target.value }))} />
          <GoldButton onClick={addVideo} disabled={!eventId || !albumId || !video.url} data-testid="video-add-btn">
            Añadir video
          </GoldButton>
        </div>
        <p className="mt-3 text-xs text-[#5A5752]">
          Se guarda la miniatura y el reproductor embebido; el video permanece en tu canal HR Creations.
        </p>
      </div>
    </div>
  );
}
